import { useEffect, useRef, useState } from "react";

const SCRIPT_SRC =
  "https://link.crecelocal.com/reputation/assets/review-widget.js";
const IFRAME_SRC =
  "https://link.crecelocal.com/reputation/widgets/review_widget/UmeUICnWy44Ung9XrMjl?widgetId=694a9ed3d94a40e4c41d582b";

type Props = {
  /** Optional heading rendered above the widget */
  title?: string;
  className?: string;
};

/**
 * Embeds the CreceLocal Google review widget (script + iframe).
 * The external script injects styles/height logic into the iframe,
 * so we load it once and keep the iframe mounted.
 */
export const GoogleReviewsWidget = ({ title, className }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);

  useEffect(() => {
    // Only load the external reputation script and iframe when near the viewport
    const el = containerRef.current;
    if (!el) return;

    if (!("IntersectionObserver" in window)) {
      setShouldLoad(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldLoad) return;
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) return;

    const script = document.createElement("script");
    script.type = "text/javascript";
    script.src = SCRIPT_SRC;
    script.async = true;
    document.body.appendChild(script);
  }, [shouldLoad]);

  return (
    <div ref={containerRef} className={className}>
      {title && (
        <h2 className="text-2xl md:text-3xl font-medium tracking-tight text-foreground mb-8 text-center">
          {title}
        </h2>
      )}
      {shouldLoad ? (
        <iframe
          className="lc_reviews_widget"
          src={IFRAME_SRC}
          frameBorder={0}
          scrolling="no"
          title="Reseñas de Google de clientes de CreceLocal"
          loading="lazy"
          style={{ minWidth: "100%", width: "100%", border: "none" }}
        />
      ) : (
        <div className="h-64 flex items-center justify-center text-[#71717A] text-sm">
          Cargando opiniones de Google...
        </div>
      )}
    </div>
  );
};
